import {Color} from '@benchling/nucleus-ui';
import {createStyles, Group, Text} from '@mantine/core';
import React from 'react';

import {
  ProcedureRunDataEntryMode,
  ProcedureType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcedureChip from 'ui-library/chips/ProcedureChip';

import MultiBatchSelector from '../process-development/multi-batch-selector/MultiBatchSelector';
import {getTypedStudyAdapter} from '../TypedStudyAdapters';
import {
  EditNumConditionGroupsButtons,
  planConditionGroupsButtonVariant,
  runConditionGroupsButtonVariant,
} from './EditNumConditionGroupsButtons';
import MethodExecutionLimitError from './MethodExecutionLimitError';
import ProcedureCard from './ProcedureCard';
import {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';
import {TransposedReplicateTable} from './TransposedReplicateTable';
import {ConditionGroup} from './useExperimentDesignState';
import {MethodExecutionLimitState} from './useMethodExecutionLimitState';

interface BaseProcedureRunDesignEditorLeftSidepanelProps<TCondition> {
  isPlan: boolean;
  conditions: Array<TCondition>;
  setConditions: (conditions: Array<TCondition>, prevIndexes: Array<number | null>) => void;
  experimentalInputs: ExperimentalInputs;
  setExperimentalInputs: (experimentalInputs: ExperimentalInputs) => void;
  editState: StudyDesignTabEditState;
  procedureType: ProcedureType;
  methodExecutionLimitState: MethodExecutionLimitState;
  studyType: StudyType;
  dataEntryMode: ProcedureRunDataEntryMode;
  procedureId: string;
  flowchartVersionId: string;
  newConditionCoveredMethodIds?: ReadonlyArray<string>;
}

interface ProcedureRunDesignEditorLeftSidepanelPlanProps extends BaseProcedureRunDesignEditorLeftSidepanelProps<ConditionGroup> {
  isPlan: true;
  onRemoveProcedure: () => Promise<void>;
  setDataEntryMode: (dataEntryMode: ProcedureRunDataEntryMode) => void;
  hasUpdateAvailable: boolean;
  onOpenUpdateModal: () => void;
}

interface ProcedureRunDesignEditorLeftSidepanelRunProps extends BaseProcedureRunDesignEditorLeftSidepanelProps<ConditionGroup> {
  isPlan: false;
  isSplitPoolEnabled: boolean;
  activeMethodName: string | null;
  doesActiveMethodHaveDownstreamMethods: boolean;
}

type ProcedureRunDesignEditorLeftSidepanelProps =
  | ProcedureRunDesignEditorLeftSidepanelPlanProps
  | ProcedureRunDesignEditorLeftSidepanelRunProps;

function AddRemoveConditionsInfoBanner({
  isSplitPoolEnabled,
  activeMethodName,
  doesActiveMethodHaveDownstreamMethods,
}: {
  isSplitPoolEnabled: boolean;
  activeMethodName?: string | null;
  doesActiveMethodHaveDownstreamMethods?: boolean;
}): JSX.Element | null {
  const {classes} = useStyles();
  // No active method
  if (isSplitPoolEnabled && activeMethodName == null) {
    return null;
  }
  const legacyAddConditionsBannerText = 'New worksheets will be created for new conditions.';
  const splitPoolBannerText = (
    <span className={classes.splitPoolBannerText}>
      Adding or removing condition replicates applies to{' '}
      <span className={classes.splitPoolActiveMethodName}>{activeMethodName}</span>
      {doesActiveMethodHaveDownstreamMethods ? ' and all downstream operations.' : '.'}
    </span>
  );
  return (
    <div className={classes.infoBannerWrapper}>
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        supportingInfo={isSplitPoolEnabled ? splitPoolBannerText : legacyAddConditionsBannerText}
      />
    </div>
  );
}

export default function ProcedureRunDesignEditorLeftSidepanel(
  props: ProcedureRunDesignEditorLeftSidepanelProps
): JSX.Element {
  const {classes} = useStyles();
  const isLocked = props.editState !== StudyDesignTabEditState.EDITING;
  const isEditable = props.isPlan && !isLocked;
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, props.procedureType, {
    stringCase: StringCase.LOWER,
  });

  const editNumColsButtons = (
    <EditNumConditionGroupsButtons
      conditions={props.conditions}
      setConditions={props.setConditions}
      methodExecutionLimitState={props.methodExecutionLimitState}
      procedureType={props.procedureType}
      showReplicateLimitLabel={false}
      newConditionCoveredMethodIds={props.newConditionCoveredMethodIds}
      {...(props.isPlan
        ? planConditionGroupsButtonVariant({
            conditions: props.conditions,
            methodExecutionLimitState: props.methodExecutionLimitState,
            procedureType: props.procedureType,
          })
        : runConditionGroupsButtonVariant({
            conditions: props.conditions,
            methodExecutionLimitState: props.methodExecutionLimitState,
            procedureType: props.procedureType,
          }))}
    />
  );
  const replicateTableComponent = (
    <>
      <TransposedReplicateTable
        conditions={props.conditions}
        setConditions={props.setConditions}
        experimentalInputs={props.experimentalInputs}
        setExperimentalInputs={props.setExperimentalInputs}
        editNumColsButtons={editNumColsButtons}
        locked={isLocked}
        procedureType={props.procedureType}
        newConditionCoveredMethodIds={props.newConditionCoveredMethodIds}
        isPlan={props.isPlan}
        dataTestAttrs={{component: 'TransposedReplicateTable', element: 'replicateTable'}}
      />
      {props.methodExecutionLimitState.isOverLimit && (
        <MethodExecutionLimitError
          replicateLimit={props.methodExecutionLimitState.replicateLimit}
          procedureType={props.procedureType}
        />
      )}
    </>
  );

  if (props.isPlan) {
    return (
      <div className={classes.sidepanelContent}>
        {replicateTableComponent}
        <div className={classes.procedureCardWrapper}>
          <Text className={classes.procedureCardLabel}>{`Active ${procedureString}`}</Text>
          <ProcedureCard
            procedureId={props.procedureId}
            flowchartVersionId={props.flowchartVersionId}
            procedureType={props.procedureType}
            studyType={props.studyType}
            onRemoveProcedure={props.onRemoveProcedure}
            isEditable={isEditable}
            hasUpdateAvailable={props.hasUpdateAvailable}
            onOpenUpdateModal={props.onOpenUpdateModal}
          />
        </div>
        {getTypedStudyAdapter(props.studyType).isMultiBatchEnabled && (
          <div className={classes.multiBatchSelectorWrapper}>
            <MultiBatchSelector
              dataEntryMode={props.dataEntryMode}
              onSelectDataEntryMode={props.setDataEntryMode}
              isDisabled={!isEditable}
              withContainerPadding={false}
            />
          </div>
        )}
      </div>
    );
  }
  return (
    <div className={classes.sidepanelContent}>
      {props.editState === StudyDesignTabEditState.EDITING && (
        <AddRemoveConditionsInfoBanner
          isSplitPoolEnabled={props.isSplitPoolEnabled}
          activeMethodName={props.activeMethodName}
          doesActiveMethodHaveDownstreamMethods={props.doesActiveMethodHaveDownstreamMethods}
        />
      )}
      {replicateTableComponent}
      <div className={classes.runSectionWrapper}>
        <Group spacing="xs" noWrap={true} className={classes.activeProcedureRow}>
          <Text className={classes.activeProcedureLabel}>{`Active ${procedureString}:`}</Text>
          <ProcedureChip
            size={ChipSize.MINI}
            id={props.procedureId}
            flowchartVersionId={props.flowchartVersionId}
          />
        </Group>
      </div>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  sidepanelContent: {
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
  },
  infoBannerWrapper: {
    marginTop: theme.spacing.md,
  },
  procedureCardWrapper: {
    marginTop: theme.spacing.md,
    borderTop: `1px solid ${Color.GRAY_200}`,
    paddingTop: theme.spacing.md,
  },
  multiBatchSelectorWrapper: {
    marginTop: theme.spacing.xl,
  },
  runSectionWrapper: {
    marginTop: theme.spacing.md,
    borderTop: `1px solid ${Color.GRAY_200}`,
    paddingTop: theme.spacing.md,
  },
  activeProcedureRow: {
    marginTop: 0,
  },
  activeProcedureLabel: {
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '14px',
  },
  procedureCardLabel: {
    color: Color.BLACK,
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    lineHeight: '14px',
    marginBottom: theme.spacing.md,
  },
  splitPoolBannerText: {
    fontWeight: 400,
  },
  splitPoolActiveMethodName: {
    fontWeight: 600,
  },
}));
